import { Button, HorizontalStack, Icon, Link, Progress, Text, VerticalStack } from '@lego/klik-ui';
import {
  ControlClose,
  StatusCircleCheckSolid,
  StatusCircleExclamationSolid,
} from '@lego/klik-ui/icons';

export type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

type Props = {
  label: string;
  status: SubmitStatus;
  errorMessage?: string | null;
  submittingText?: string;
  successText?: string;
  onReset: () => void;
  onRetry: () => void;
};

export const SubmitStatusIndicator = ({
  label,
  status,
  errorMessage,
  submittingText = 'Submitting...',
  successText = 'Success',
  onReset,
  onRetry,
}: Props) => {
  if (status === 'idle') return null;

  return (
    <VerticalStack spacing={1}>
      <HorizontalStack alignItems="center" spacing={2}>
        <Text fontSize="md" fontWeight="medium">
          {label}
        </Text>
        {status === 'success' ? (
          <Icon alignSelf="center" as={StatusCircleCheckSolid} color="success.500" h={6} w={6} />
        ) : (
          <Button
            alignSelf="center"
            aria-label="Cancel"
            colorScheme="slate"
            leftIcon={<ControlClose />}
            minH="fit-content"
            minW="fit-content"
            onClick={onReset}
            size="sm"
            variant="ghost"
          />
        )}
      </HorizontalStack>

      {status === 'submitting' && (
        <>
          <Progress borderRadius="full" isIndeterminate />
          <Text color="slate.500" fontSize="sm">
            {submittingText}
          </Text>
        </>
      )}

      {status === 'success' && (
        <>
          <Progress borderRadius="full" value={100} />
          <Text color="slate.500" fontSize="sm">
            {successText}
          </Text>
        </>
      )}

      {status === 'error' && (
        <>
          <HorizontalStack color="error.500" spacing={2}>
            <Icon as={StatusCircleExclamationSolid} />
            <Text fontSize="sm">{errorMessage ?? 'Operation failed'}</Text>
          </HorizontalStack>
          <Link isInline onClick={onRetry} size="xs">
            Retry
          </Link>
        </>
      )}
    </VerticalStack>
  );
};
